import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Button, FormGroup } from '@/design-system'
import { useAlbumLibrary } from '@/features/album/hooks/useAlbumLibrary'

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const StatGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
  marginBottom: 18,
})

const StatTile = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: 18,
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  cursor: 'pointer',
  transition: `all var(--ds-duration-normal) var(--ds-ease)`,
  '&:hover': { boxShadow: 'var(--ds-shadow-lift)', transform: 'translateY(-2px)' },
})

const Ic = styled(Box)({
  width: 42,
  height: 42,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 18,
  flexShrink: 0,
})

const N = styled('div')({ fontSize: 24, fontWeight: 750, letterSpacing: '-0.03em', lineHeight: 1, color: 'var(--ds-ink)' })
const L = styled('div')({ fontSize: 12.5, color: 'var(--ds-ink-subtle)', marginTop: 3 })
const Go = styled('span')({ marginLeft: 'auto', color: 'var(--ds-ink-subtle)' })

const StorageRow = styled(Box)({ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 })
const StorageValue = styled('span')({ fontFamily: 'var(--ds-font-mono)', fontSize: 11, color: 'var(--ds-ink-subtle)' })
const StorageBar = styled(Box)({ height: 8, borderRadius: 4, background: 'var(--ds-hover)', overflow: 'hidden' })
const StorageFill = styled(Box)({ height: '100%', borderRadius: 4, background: 'var(--ds-gradient-spectrum, var(--ds-ink))' })
const StorageMeta = styled(Box)({ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 10, fontSize: 12.5, color: 'var(--ds-ink-subtle)' })

/**
 * Real album/gallery counts and photo totals from `useAlbumLibrary` (summed from each
 * album's real `photoCount`). There's no storage-quota endpoint (no GB used/limit
 * anywhere in the API), so the bar is a static "Unlimited" fill rather than a fabricated
 * percentage — the one storage claim that's true regardless of usage: full-res, always.
 */
export function ContentSection() {
  const navigate = useNavigate()
  const { albums, loading } = useAlbumLibrary()
  const galleryCount = albums.filter((a) => a.galleryId).length
  const totalPhotos = albums.reduce((sum, a) => sum + (a.photoCount || 0), 0)

  return (
    <Box>
      <PanelHead>
        <H2>Content &amp; Storage</H2>
        <Sub>What you&rsquo;ve made, and how much room it takes.</Sub>
      </PanelHead>

      <FormGroup>
        <StorageRow>
          <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ds-ink)' }}>Storage</Typography>
          <StorageValue>Unlimited</StorageValue>
        </StorageRow>
        <StorageBar>
          <StorageFill sx={{ width: '100%' }} />
        </StorageBar>
        <StorageMeta>
          <span>{loading ? 'Loading…' : `${totalPhotos} total photo${totalPhotos === 1 ? '' : 's'} across ${albums.length} album${albums.length === 1 ? '' : 's'}`}</span>
          <span>Full resolution — always</span>
        </StorageMeta>
      </FormGroup>

      <StatGrid>
        <StatTile onClick={() => navigate('/albums')}>
          <Ic sx={{ background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))', color: 'var(--ds-accent-deep-a, var(--ds-ink))' }}>▤</Ic>
          <Box>
            <N>{loading ? '—' : albums.length}</N>
            <L>Albums</L>
          </Box>
          <Go>→</Go>
        </StatTile>
        <StatTile onClick={() => navigate('/galleries')}>
          <Ic sx={{ background: 'var(--ds-gradient-spectrum-soft, var(--ds-hover))', color: 'var(--ds-accent-deep-b, var(--ds-ink))' }}>◈</Ic>
          <Box>
            <N>{loading ? '—' : galleryCount}</N>
            <L>3D Galleries</L>
          </Box>
          <Go>→</Go>
        </StatTile>
      </StatGrid>

      <FormGroup label='Quick actions'>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button variant='primary' size='sm' onClick={() => navigate('/albums/new')}>
            ＋ New album
          </Button>
          <Button variant='secondary' size='sm' onClick={() => navigate('/galleries/try-3d')}>
            ◈ New 3D gallery
          </Button>
          <Button variant='secondary' size='sm' onClick={() => toast('Coming soon')}>
            ↑ Review guest uploads
          </Button>
        </Box>
      </FormGroup>
    </Box>
  )
}
